import { useState, useEffect, useCallback } from 'react';

export interface MetalPrices {
  city: string;
  gold_24k: number;
  gold_22k: number;
  silver_per_kg: number;
  updated_at: string;
  source?: {
    gold_usd: number;
    silver_usd: number;
    usd_to_inr: number;
  };
}

export interface MetalPricesState {
  data: MetalPrices | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

interface UseLiveMetalPricesOptions {
  pollingInterval?: number; // in milliseconds, default 60000 (1 minute)
  autoRefresh?: boolean; // default true
}

/**
 * Custom hook to fetch and manage live metal prices
 */
export function useLiveMetalPrices(options: UseLiveMetalPricesOptions = {}) {
  const { pollingInterval = 60000, autoRefresh = true } = options;
  
  const [state, setState] = useState<MetalPricesState>({
    data: null,
    loading: true,
    error: null,
    lastUpdated: null
  });

  const fetchPrices = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await fetch('/api/metal-prices');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data: MetalPrices = await response.json();
      
      setState({
        data,
        loading: false,
        error: null,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Failed to fetch metal prices:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch prices'
      }));
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  // Polling
  useEffect(() => {
    if (!autoRefresh) return;
    
    const intervalId = setInterval(fetchPrices, pollingInterval);
    
    return () => clearInterval(intervalId);
  }, [autoRefresh, pollingInterval, fetchPrices]);

  return {
    ...state,
    refetch: fetchPrices
  };
}
